import { api } from "./api";

export type Status = "active" | "inactive";

export type ModelProvider = "genbi" | "openai" | "aws" | "deepseek";

export type TenantAdminDTO = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: string | undefined;
  status: Status;
  created_at: Date;
  role: "tenant_admin";
};

export type RootAdminDTO = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: Status;
  role: "root_admin";
  created_at: Date;
};

export type AdminDTO = RootAdminDTO | TenantAdminDTO;

export type ModelRecord = {
  id: string;
  name: string;
  model_id: string;
};

export type AWSAISettings = {
  aws_access_key: string;
  aws_secret_key: string;
  aws_assume_role_arn: string;
  aws_region: string;
  models: ModelRecord[];
};

export type OpenaiAISettings = {
  openai_api_key: string;
  openai_project: string;
  models: ModelRecord[];
};

export type GenbiAISettings = {
  openai_api_key: string;
  openai_project: string;
  models: ModelRecord[];
};

export type DeepseekAISettings = {
  deepseek_api_key: string;
  models: ModelRecord[];
};

export type TenantRecord = {
  id: string;
  tenant_name: string;
  tenant_code: string;
  max_users: number;
  valid_until: Date;
  model_provider: ModelProvider;
  aws_settings: AWSAISettings | undefined;
  openai_settings: OpenaiAISettings | undefined;
  deepseek_settings: DeepseekAISettings | undefined;
  genbi_settings: GenbiAISettings | undefined;
  status: Status;
  created_at: Date;
};

export type CreateTenantDTO = {
  tenant_name: string;
  tenant_code: string;
  max_users: number;
  valid_until: Date;
  model_provider: ModelProvider;
  aws_settings?: Omit<AWSAISettings, "provider">;
  openai_settings?: Omit<OpenaiAISettings, "provider">;
  genbi_settings?: Omit<GenbiAISettings, "provider">;
  deepseek_settings?: Omit<DeepseekAISettings, "provider">;
  status: Status;
};

export type CreateAdminDTO = {
  email: string;
  first_name: string;
  last_name: string;
  role: "root_admin" | "tenant_admin";
  tenant_id?: string;
  status: Status;
};

export type UpdateTenantDTO = {
  tenant_name?: string;
  tenant_code?: string;
  max_users?: number;
  valid_until?: Date;
  model_provider?: ModelProvider;
  aws_settings?: AWSAISettings;
  openai_settings?: OpenaiAISettings;
  genbi_settings?: GenbiAISettings;
  deepseek_settings?: DeepseekAISettings;
  status?: Status;
};

export type UpdateAdminDTO = {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: "root_admin" | "tenant_admin";
  tenant_id?: string;
  status?: Status;
};

export const adminService = {
  /**
   * Get a list of all admin users
   * @returns Promise containing an array of admin users
   */
  getAdminList: async (): Promise<AdminDTO[]> => {
    const response = await api.get<AdminDTO[]>("/admin/list");
    return response.data;
  },

  /**
   * Create a new admin user
   * @param data The admin user data to create
   * @returns Promise containing the created admin user
   */
  createAdmin: async (data: CreateAdminDTO): Promise<AdminDTO> => {
    const response = await api.post<AdminDTO>("/admin/create", data);
    return response.data;
  },

  /**
   * Update an admin user's information
   * @param id The admin ID
   * @param data The admin data to update
   * @returns Promise containing the updated admin record
   */
  updateAdmin: async (id: string, data: UpdateAdminDTO): Promise<AdminDTO> => {
    const response = await api.put<AdminDTO>(`/admin/${id}`, data);
    return response.data;
  },
};

export const tenantService = {
  /**
   * Get a list of all tenants
   * @returns Promise containing an array of tenant records
   */
  getTenantList: async (): Promise<TenantRecord[]> => {
    const response = await api.get<TenantRecord[]>("/tenants");
    return response.data;
  },

  /**
   * Get a specific tenant by ID
   * @param id The tenant ID
   * @returns Promise containing the tenant record
   */
  getTenantById: async (id: string): Promise<TenantRecord> => {
    const response = await api.get<TenantRecord>(`/tenants/${id}`);
    return response.data;
  },

  /**
   * Create a new tenant
   * @param data The tenant data to create
   * @returns Promise containing the created tenant record
   */
  createTenant: async (data: CreateTenantDTO): Promise<TenantRecord> => {
    const response = await api.post<TenantRecord>("/tenants", data);
    return response.data;
  },

  /**
   * Delete a tenant by ID
   * @param id The tenant ID to delete
   * @returns Promise that resolves when the tenant is deleted
   */
  deleteTenant: async (id: string): Promise<void> => {
    await api.delete(`/tenants/${id}`);
  },

  /**
   * Update a tenant's information
   * @param id The tenant ID
   * @param data The tenant data to update
   * @returns Promise containing the updated tenant record
   */
  updateTenant: async (
    id: string,
    data: UpdateTenantDTO,
  ): Promise<TenantRecord> => {
    const response = await api.put<TenantRecord>(`/tenants/${id}`, data);
    return response.data;
  },
};
